<template>
  <div class="performance-overview">
    <h2>{{ t('simulation.performance.title') }}</h2>
    <div class="metrics-grid">
      <div class="metric-card profit">
        <div class="metric-icon">💰</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.totalProfit') }}</h4>
          <p class="metric-value" :class="getProfitClass(performanceMetrics?.total_profit_sum)">
            {{ formatPercentage(performanceMetrics?.total_profit_sum || 0) }}
          </p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">📊</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.winRate') }}</h4>
          <p class="metric-value">{{ formatPercentage((performanceMetrics?.win_rate_percent || 0) / 100) }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">🔄</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.orderCount') }}</h4>
          <p class="metric-value">{{ performanceMetrics?.total_order_count || 0 }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">📅</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.averageHoldingDays') }}</h4>
          <p class="metric-value">{{ formatNumber(performanceMetrics?.average_holding_period_days || 0) }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">📈</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.profitLossRatio') }}</h4>
          <p class="metric-value">{{ formatNumber(performanceMetrics?.profit_loss_ratio || 0) }}</p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">📊</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.averageReturn') }}</h4>
          <p class="metric-value" :class="getProfitClass(performanceMetrics?.average_return_percent_per_trade)">
            {{ formatPercentage((performanceMetrics?.average_return_percent_per_trade || 0) / 100) }}
          </p>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon">📉</div>
        <div class="metric-content">
          <h4>{{ t('simulation.performance.metrics.returnStdDev') }}</h4>
          <p class="metric-value">{{ formatPercentage((performanceMetrics?.return_std_dev_percent_per_trade || 0) / 100) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

defineProps({
  performanceMetrics: {
    type: Object,
    default: () => ({})
  }
});

const { t } = useI18n()

const formatNumber = (value) => {
  if (value == null) return t('common.notAvailable')
  return typeof value === 'number' ? value.toFixed(2) : value;
};

const formatPercentage = (value) => {
  if (value == null) return t('common.notAvailable')
  return `${(value * 100).toFixed(2)}%`
};

const getProfitClass = (value) => {
  if (value == null || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
};
</script>

<style scoped>
.performance-overview {
  margin-bottom: 32px;
}

.performance-overview h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.metric-icon {
  font-size: 24px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 50%;
}

.metric-content h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #8c8c8c;
  font-weight: 500;
}

.metric-value {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.metric-value.positive { color: #52c41a; }
.metric-value.negative { color: #ff4d4f; }
.metric-value.neutral { color: #8c8c8c; }
</style>
